import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { IoArrowBack, IoEyeOutline, IoEyeOffOutline } from "react-icons/io5";
import { useAuth } from "@/context/AuthContext";
import { getPageConfig } from "@/lib/api";

const RED = "var(--brand)";

/**
 * Première arrivée = onboarding conversationnel mené par Nerodus (une étape à la fois).
 * Profil déjà complété = formulaire d'édition (profil + réponses au questionnaire + visibilité).
 * Les réponses au questionnaire s'affichent ensuite sur le profil du membre.
 */
export default function Profil() {
  const nav = useNavigate();
  const location = useLocation();
  const preview = new URLSearchParams(location.search).get("apercu") === "1";
  const { user, updateProfile, loading, isAuthenticated } = useAuth();
  const fileRef = useRef(null);

  const [cfg, setCfg] = useState(null);
  const [nerodusImg, setNerodusImg] = useState("/nerodus.png");

  const [avatar, setAvatar] = useState(user?.avatar || "");
  const [name, setName] = useState(user?.name || "");
  const [instagram, setInstagram] = useState(user?.instagram || "");
  const [bio, setBio] = useState(user?.bio || "");
  const [answers, setAnswers] = useState(user?.questionnaire || {});
  const [isPublic, setIsPublic] = useState(user?.questionnaire_public !== false);
  const [busy, setBusy] = useState(false);

  const isEdit = !!user?.profile_completed;
  const [step, setStep] = useState(0); // onboarding step index
  useEffect(() => {
    if (!loading && !isAuthenticated) nav("/connexion", { replace: true });
  }, [loading, isAuthenticated, nav]);

  useEffect(() => {
    getPageConfig("questionnaire").then(setCfg).catch(() => setCfg({ questions: [] }));
    getPageConfig("nerodus").then((n) => n?.image && setNerodusImg(n.image)).catch(() => {});
  }, []);

  useEffect(() => {
    if (user) {
      setAvatar(user.avatar || ""); setName(user.name || "");
      setInstagram(user.instagram || ""); setBio(user.bio || "");
      setAnswers(user.questionnaire || {}); setIsPublic(user.questionnaire_public !== false);
    }
  }, [user]);

  const questions = cfg?.questions || [];

  const pickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 3 * 1024 * 1024) { toast.error("Image trop lourde (max 3 Mo)."); return; }
    const reader = new FileReader();
    reader.onload = () => setAvatar(reader.result);
    reader.readAsDataURL(f);
  };

  const save = async (goHub) => {
    if (preview) { toast.success("Aperçu terminé ✓"); nav("/parametres"); return; }
    if (!name.trim()) { toast.error("Choisis un nom."); return; }
    setBusy(true);
    try {
      await updateProfile({
        name: name.trim(), avatar: avatar || null, instagram: instagram || null,
        bio: bio || null, questionnaire: answers, questionnaire_public: isPublic,
      });
      toast.success(isEdit ? "Profil mis à jour ✓" : "Bienvenue au Dōjō ! 🥋");
      if (goHub) {
        // Nerodus propose sa visite guidée juste après le questionnaire (on saute l'intro cinématique).
        try { sessionStorage.setItem("sx_intro_shown", "1"); } catch (e) {}
        nav("/hub", { replace: true });
        setTimeout(() => window.dispatchEvent(new Event("td-offer-tour")), 300);
      } else nav(-1);
    } catch {
      toast.error("Impossible d'enregistrer le profil.");
    } finally { setBusy(false); }
  };

  if (!cfg) return <div style={s.page}><div style={{ color: "var(--text-secondary)" }}>Chargement…</div></div>;

  // ---------------- MODE ÉDITION ----------------
  if (isEdit && !preview) {
    return (
      <div style={s.page}>
        <div style={{ ...s.card, maxWidth: 620, textAlign: "left" }}>
          <button type="button" style={s.back} onClick={() => nav(-1)} data-testid="profil-back"><IoArrowBack /> Retour</button>
          <h1 style={{ ...s.h1, textAlign: "left" }}>Mon profil</h1>
          <p style={{ ...s.sub, textAlign: "left" }}>Modifie ton profil et tes réponses. Tout est modifiable quand tu veux.</p>

          <div style={s.editForm}>
            <button type="button" style={{ ...s.avatarBtn, alignSelf: "flex-start" }} onClick={() => fileRef.current?.click()} data-testid="profil-avatar">
              {avatar ? <img src={avatar} alt="avatar" style={s.avatarImg} /> : <span style={s.avatarPlus}>+<span style={s.avatarHint}>Photo</span></span>}
            </button>
            <input ref={fileRef} type="file" accept="image/*" onChange={pickFile} style={{ display: "none" }} />

            <Field label="Nom *"><input style={s.input} value={name} onChange={(e) => setName(e.target.value)} data-testid="profil-name" /></Field>
            <Field label="Instagram (optionnel)"><input style={s.input} value={instagram} onChange={(e) => setInstagram(e.target.value)} placeholder="@ton_compte" data-testid="profil-instagram" /></Field>
            <Field label="Présentation courte (sur ta carte de membre)">
              <textarea style={{ ...s.input, minHeight: 60, resize: "vertical" }} value={bio} onChange={(e) => setBio(e.target.value)} placeholder="En une phrase…" data-testid="profil-bio" />
            </Field>

            <div style={s.qHead}>Le questionnaire de Nerodus</div>
            {questions.map((q) => (
              <Field key={q.id} label={q.text}>
                <textarea style={{ ...s.input, minHeight: 70, resize: "vertical" }} value={answers[q.id] || ""}
                  onChange={(e) => setAnswers((a) => ({ ...a, [q.id]: e.target.value }))} data-testid={`profil-answer-${q.id}`} />
              </Field>
            ))}

            <button type="button" onClick={() => setIsPublic((v) => !v)} style={s.visToggle} data-testid="profil-visibility">
              {isPublic ? <IoEyeOutline size={18} /> : <IoEyeOffOutline size={18} />}
              <span>{isPublic ? "Mes réponses sont visibles par les membres" : "Mes réponses sont masquées aux membres"}</span>
              <span style={{ ...s.switch, background: isPublic ? RED : "var(--border-c)" }}><span style={{ ...s.knob, transform: isPublic ? "translateX(18px)" : "translateX(0)" }} /></span>
            </button>

            <button onClick={() => save(false)} disabled={busy} style={{ ...s.submit, opacity: busy ? 0.6 : 1 }} data-testid="profil-save">
              {busy ? "Un instant…" : "Enregistrer"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ---------------- MODE ONBOARDING (conversationnel) ----------------
  const steps = [
    { key: "intro" },
    { key: "photo", say: "Pour commencer, une photo ? C'est plus sympa de mettre un visage sur un nom. (Tu peux passer si tu veux.)" },
    { key: "name", say: "Comment veux-tu qu'on t'appelle ici ?" },
    { key: "instagram", say: "Un Instagram à partager avec la communauté ? (Optionnel.)" },
    { key: "bio", say: "Décris-toi en une phrase — ce sera ta petite présentation sur ta carte de membre." },
    ...questions.map((q) => ({ key: `q_${q.id}`, qid: q.id, say: q.text })),
    { key: "outro" },
  ];
  const cur = steps[Math.min(step, steps.length - 1)];
  const total = steps.length - 1; // hors intro
  const progress = Math.round((Math.max(0, step) / (steps.length - 1)) * 100);

  const goNext = () => setStep((x) => Math.min(x + 1, steps.length - 1));
  const goBack = () => setStep((x) => Math.max(x - 1, 0));

  const NerodusBubble = ({ children }) => (
    <div style={s.chatWrap}>
      <div style={s.avatarRing}><img src={nerodusImg} alt="Nerodus" style={s.nerodusImg} /></div>
      <div style={s.bubble} data-testid="nerodus-say">
        <div style={s.bubbleName}>{cfg.name || "Nerodus"} · <span style={{ color: "var(--text-secondary)", fontWeight: 600 }}>{cfg.role || "Guide du Dōjō"}</span></div>
        <div style={s.bubbleText}>{children}</div>
      </div>
    </div>
  );

  return (
    <div style={s.page}>
      <div style={s.card}>
        <div style={s.brand}><span style={s.kanji}>天</span><span style={s.brandText}>TENGOKU <span style={{ color: RED }}>DŌJŌ</span></span></div>

        {preview && (
          <div style={s.previewBar} data-testid="onboarding-preview-bar">
            <span>👁️ Aperçu de l'onboarding — rien n'est enregistré.</span>
            <button style={s.previewExit} onClick={() => nav("/parametres")} data-testid="onboarding-preview-exit">Quitter l'aperçu</button>
          </div>
        )}

        {cur.key !== "intro" && (
          <div style={s.progressBar}><div style={{ ...s.progressFill, width: `${progress}%` }} /></div>
        )}

        {cur.key === "intro" && (
          <div style={s.introWrap} data-testid="onboarding-intro">
            <div style={s.avatarRingBig}><img src={nerodusImg} alt="Nerodus" style={s.nerodusImg} /></div>
            <h1 style={s.introGreeting}>{cfg.intro_greeting}</h1>
            <p style={s.introText}>{cfg.intro_text}</p>
            <button style={s.submit} onClick={goNext} data-testid="onboarding-start">{cfg.intro_cta || "Oui, c'est parti !"}</button>
          </div>
        )}

        {cur.key === "photo" && (
          <StepShell say={<NerodusBubble>{cur.say}</NerodusBubble>}>
            <button type="button" style={s.avatarBtn} onClick={() => fileRef.current?.click()} data-testid="onboarding-avatar">
              {avatar ? <img src={avatar} alt="avatar" style={s.avatarImg} /> : <span style={s.avatarPlus}>+<span style={s.avatarHint}>Photo</span></span>}
            </button>
            <input ref={fileRef} type="file" accept="image/*" onChange={pickFile} style={{ display: "none" }} />
            <Nav onBack={goBack} onNext={goNext} nextLabel={avatar ? "Suivant →" : "Passer"} />
          </StepShell>
        )}

        {cur.key === "name" && (
          <StepShell say={<NerodusBubble>{cur.say}</NerodusBubble>}>
            <input style={s.bigInput} value={name} onChange={(e) => setName(e.target.value)} placeholder="Ton nom d'artiste" data-testid="onboarding-name" autoFocus />
            <Nav onBack={goBack} onNext={goNext} disabled={!name.trim()} />
          </StepShell>
        )}

        {cur.key === "instagram" && (
          <StepShell say={<NerodusBubble>{cur.say}</NerodusBubble>}>
            <input style={s.bigInput} value={instagram} onChange={(e) => setInstagram(e.target.value)} placeholder="@ton_compte" data-testid="onboarding-instagram" autoFocus />
            <Nav onBack={goBack} onNext={goNext} nextLabel={instagram.trim() ? "Suivant →" : "Passer"} />
          </StepShell>
        )}

        {cur.key === "bio" && (
          <StepShell say={<NerodusBubble>{cur.say}</NerodusBubble>}>
            <textarea style={s.bigArea} value={bio} onChange={(e) => setBio(e.target.value)} placeholder="Ex : conteur d'histoires fantastiques et amoureux des mondes imaginaires." data-testid="onboarding-bio" autoFocus />
            <Nav onBack={goBack} onNext={goNext} nextLabel={bio.trim() ? "Suivant →" : "Passer"} />
          </StepShell>
        )}

        {cur.key.startsWith("q_") && (
          <StepShell say={<NerodusBubble>{cur.say}</NerodusBubble>}>
            <textarea style={s.bigArea} value={answers[cur.qid] || ""} onChange={(e) => setAnswers((a) => ({ ...a, [cur.qid]: e.target.value }))}
              placeholder="Écris ta réponse ici…" data-testid={`onboarding-answer-${cur.qid}`} autoFocus />
            <Nav onBack={goBack} onNext={goNext} nextLabel={(answers[cur.qid] || "").trim() ? "Suivant →" : "Passer"} />
          </StepShell>
        )}

        {cur.key === "outro" && (
          <div style={s.introWrap} data-testid="onboarding-outro">
            <div style={s.avatarRingBig}><img src={nerodusImg} alt="Nerodus" style={s.nerodusImg} /></div>
            <h1 style={s.introGreeting}>{cfg.outro_title || "Merci !"}</h1>
            <p style={s.introText}>{cfg.outro_text}</p>
            <button type="button" onClick={() => setIsPublic((v) => !v)} style={{ ...s.visToggle, maxWidth: 420 }} data-testid="onboarding-visibility">
              {isPublic ? <IoEyeOutline size={18} /> : <IoEyeOffOutline size={18} />}
              <span style={{ textAlign: "left" }}>{isPublic ? "Mes réponses seront visibles par les membres" : "Mes réponses resteront masquées"}</span>
              <span style={{ ...s.switch, background: isPublic ? RED : "var(--border-c)" }}><span style={{ ...s.knob, transform: isPublic ? "translateX(18px)" : "translateX(0)" }} /></span>
            </button>
            <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
              <button style={s.ghost} onClick={goBack} data-testid="onboarding-back">Précédent</button>
              <button style={{ ...s.submit, opacity: busy ? 0.6 : 1 }} onClick={() => save(true)} disabled={busy} data-testid="onboarding-finish">
                {busy ? "Un instant…" : "Entrer dans le Dōjō →"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div style={s.field}><label style={s.label}>{label}</label>{children}</div>
);
const StepShell = ({ say, children }) => (
  <div style={s.stepShell}>{say}<div style={s.stepBody}>{children}</div></div>
);
const Nav = ({ onBack, onNext, nextLabel = "Suivant →", disabled }) => (
  <div style={s.navRow}>
    <button style={s.ghost} onClick={onBack} data-testid="onboarding-back">Précédent</button>
    <button style={{ ...s.submit, opacity: disabled ? 0.4 : 1, marginTop: 0 }} onClick={onNext} disabled={disabled} data-testid="onboarding-next">{nextLabel}</button>
  </div>
);

const s = {
  page: { minHeight: "100vh", background: "var(--bg-primary, #fff)", color: "var(--text-primary,#111)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24, fontFamily: "'Inter', system-ui, sans-serif" },
  card: { width: "100%", maxWidth: 560, background: "var(--bg-secondary)", border: "1px solid var(--border-c)", borderRadius: 22, boxShadow: "0 24px 70px rgba(0,0,0,0.12)", padding: "34px 30px", textAlign: "center" },
  brand: { display: "flex", alignItems: "center", justifyContent: "center", gap: 10, marginBottom: 20 },
  kanji: { fontSize: 26, fontWeight: 700 },
  brandText: { fontSize: 15, fontWeight: 800, letterSpacing: 2 },
  back: { display: "inline-flex", alignItems: "center", gap: 6, border: "none", background: "transparent", color: "var(--text-secondary)", cursor: "pointer", fontWeight: 700, fontSize: 13, marginBottom: 8 },
  h1: { fontSize: 26, fontWeight: 900, margin: "0 0 4px" },
  sub: { color: "var(--text-secondary)", fontSize: 14, margin: "0 0 20px" },

  progressBar: { height: 6, background: "var(--border-c)", borderRadius: 999, overflow: "hidden", marginBottom: 22 },
  progressFill: { height: "100%", background: RED, transition: "width .3s ease" },

  introWrap: { display: "flex", flexDirection: "column", alignItems: "center", gap: 16, padding: "8px 0 4px" },
  avatarRingBig: { width: 128, height: 128, borderRadius: "50%", overflow: "hidden", border: `3px solid ${RED}`, boxShadow: `0 0 45px color-mix(in srgb, var(--brand) 40%, transparent)`, background: "var(--bg-primary)", flexShrink: 0 },
  avatarRing: { width: 64, height: 64, borderRadius: "50%", overflow: "hidden", border: `2px solid ${RED}`, background: "var(--bg-primary)", flexShrink: 0 },
  nerodusImg: { width: "100%", height: "100%", objectFit: "cover", objectPosition: "top center" },
  introGreeting: { fontSize: 24, fontWeight: 900, margin: 0, fontFamily: "var(--font-display)" },
  introText: { fontSize: 15, lineHeight: 1.65, color: "var(--text-secondary)", margin: 0, maxWidth: 440 },

  stepShell: { display: "flex", flexDirection: "column", gap: 18, alignItems: "stretch", textAlign: "left" },
  chatWrap: { display: "flex", gap: 12, alignItems: "flex-start" },
  bubble: { flex: 1, background: "color-mix(in srgb, var(--brand) 7%, transparent)", border: "1px solid color-mix(in srgb, var(--brand) 20%, transparent)", borderRadius: "4px 16px 16px 16px", padding: "12px 15px" },
  bubbleName: { fontSize: 12, fontWeight: 800, color: RED, marginBottom: 4 },
  bubbleText: { fontSize: 15.5, lineHeight: 1.55, color: "var(--text-primary)", fontWeight: 600 },
  stepBody: { display: "flex", flexDirection: "column", gap: 16, alignItems: "center" },

  bigInput: { width: "100%", padding: "14px 16px", border: "1.5px solid var(--border-c)", borderRadius: 12, fontSize: 16, outline: "none", color: "var(--text-primary)", background: "var(--bg-primary)", fontFamily: "inherit", boxSizing: "border-box" },
  bigArea: { width: "100%", padding: "14px 16px", border: "1.5px solid var(--border-c)", borderRadius: 12, fontSize: 15.5, outline: "none", color: "var(--text-primary)", background: "var(--bg-primary)", fontFamily: "inherit", minHeight: 110, resize: "vertical", boxSizing: "border-box", lineHeight: 1.5 },
  navRow: { display: "flex", justifyContent: "space-between", gap: 12, width: "100%", marginTop: 4 },

  form: { display: "flex", flexDirection: "column", gap: 16, alignItems: "stretch" },
  editForm: { display: "flex", flexDirection: "column", gap: 16, alignItems: "stretch", textAlign: "left", marginTop: 8 },
  field: { display: "flex", flexDirection: "column", gap: 6, textAlign: "left" },
  label: { fontSize: 13, fontWeight: 700, color: "var(--text-secondary)" },
  input: { padding: "12px 14px", border: "1.5px solid var(--border-c)", borderRadius: 10, fontSize: 14, outline: "none", color: "var(--text-primary)", fontFamily: "inherit", background: "var(--bg-primary)", boxSizing: "border-box" },
  qHead: { fontSize: 15, fontWeight: 900, color: "var(--text-primary)", marginTop: 8, borderTop: "1px solid var(--border-c)", paddingTop: 16, fontFamily: "var(--font-display)" },

  avatarBtn: { alignSelf: "center", width: 118, height: 118, borderRadius: "50%", border: `2px dashed color-mix(in srgb, var(--brand) 45%, transparent)`, background: "color-mix(in srgb, var(--brand) 8%, transparent)", cursor: "pointer", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center" },
  avatarImg: { width: "100%", height: "100%", objectFit: "cover" },
  avatarPlus: { display: "flex", flexDirection: "column", alignItems: "center", color: RED, fontSize: 30, fontWeight: 700 },
  avatarHint: { fontSize: 12, fontWeight: 600 },

  visToggle: { display: "flex", alignItems: "center", gap: 10, padding: "12px 14px", borderRadius: 12, border: "1px solid var(--border-c)", background: "var(--bg-primary)", color: "var(--text-primary)", cursor: "pointer", fontSize: 13.5, fontWeight: 600, width: "100%", boxSizing: "border-box" },
  switch: { marginLeft: "auto", width: 38, height: 20, borderRadius: 999, position: "relative", flexShrink: 0, transition: "background .2s" },
  knob: { position: "absolute", top: 2, left: 2, width: 16, height: 16, borderRadius: "50%", background: "#fff", transition: "transform .2s", boxShadow: "0 1px 3px rgba(0,0,0,0.3)" },

  submit: { marginTop: 6, padding: "13px 24px", borderRadius: 12, border: "none", background: RED, color: "#fff", fontWeight: 800, cursor: "pointer", fontSize: 15, boxShadow: `0 8px 22px color-mix(in srgb, var(--brand) 27%, transparent)` },
  ghost: { padding: "13px 20px", borderRadius: 12, border: "1.5px solid var(--border-c)", background: "var(--bg-primary)", color: "var(--text-secondary)", fontWeight: 700, cursor: "pointer", fontSize: 14 },
  previewBar: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap", padding: "10px 14px", marginBottom: 18, borderRadius: 12, background: "color-mix(in srgb, var(--brand) 10%, transparent)", border: "1px dashed color-mix(in srgb, var(--brand) 40%, transparent)", color: "var(--text-primary)", fontSize: 13, fontWeight: 700 },
  previewExit: { border: "none", background: RED, color: "#fff", fontWeight: 800, fontSize: 12.5, padding: "6px 12px", borderRadius: 999, cursor: "pointer" },
};
